/**
 * SEO utility functions for generating meta tags and structured data
 */

export interface SEOConfig {
  siteName: string;
  siteUrl: string;
  defaultTitle: string;
  defaultDescription: string;
  defaultImage: string;
  twitterHandle?: string;
  locale?: string;
}

export const defaultSEOConfig: SEOConfig = {
  siteName: 'Atlantic Music Festival',
  siteUrl: import.meta.env.VITE_SITE_URL || 'https://atlanticmusicfestival.org',
  defaultTitle: 'Atlantic Music Festival',
  defaultDescription: 'Atlantic Music Festival is a premier summer music festival and institute offering world-class performances and educational opportunities for musicians.',
  defaultImage: '/og-image.jpg',
  twitterHandle: '@amfestival',
  locale: 'en_US',
};

/**
 * Generate a complete page title with site name
 */
export function generatePageTitle(title?: string, config: SEOConfig = defaultSEOConfig): string {
  if (!title || title === config.siteName) {
    return config.siteName;
  }
  return `${title} | ${config.siteName}`;
}

/**
 * Generate canonical URL
 */
export function generateCanonicalUrl(path: string, config: SEOConfig = defaultSEOConfig): string {
  const baseUrl = config.siteUrl.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Generate Open Graph image URL
 */
export function generateImageUrl(imagePath: string, config: SEOConfig = defaultSEOConfig): string {
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const baseUrl = config.siteUrl.replace(/\/$/, '');
  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${baseUrl}${cleanPath}`;
}

/**
 * Generate JSON-LD structured data for Organization
 */
export function generateOrganizationSchema(config: SEOConfig = defaultSEOConfig) {
  return {
    '@context': 'https://schema.org',
    '@type': 'MusicEvent',
    name: config.siteName,
    description: config.defaultDescription,
    url: config.siteUrl,
    image: generateImageUrl(config.defaultImage, config),
    organizer: {
      '@type': 'Organization',
      name: config.siteName,
      url: config.siteUrl,
    },
  };
}

/**
 * Generate JSON-LD structured data for a MusicEvent
 */
export interface MusicEventData {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location?: {
    name: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  performers?: Array<{
    name: string;
    role?: string;
  }>;
  image?: string;
  url?: string;
}

export function generateMusicEventSchema(
  event: MusicEventData,
  config: SEOConfig = defaultSEOConfig
) {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'MusicEvent',
    name: event.name,
    description: event.description,
    startDate: event.startDate,
    organizer: {
      '@type': 'Organization',
      name: config.siteName,
      url: config.siteUrl,
    },
  };

  if (event.endDate) {
    schema.endDate = event.endDate;
  }

  if (event.location) {
    schema.location = {
      '@type': 'Place',
      name: event.location.name,
    };

    if (event.location.address || event.location.city || event.location.state) {
      schema.location.address = {
        '@type': 'PostalAddress',
        ...(event.location.address && { streetAddress: event.location.address }),
        ...(event.location.city && { addressLocality: event.location.city }),
        ...(event.location.state && { addressRegion: event.location.state }),
        ...(event.location.country && { addressCountry: event.location.country }),
      };
    }
  }

  if (event.performers && event.performers.length > 0) {
    schema.performer = event.performers.map((performer) => ({
      '@type': 'Person',
      name: performer.name,
      ...(performer.role && { roleName: performer.role }),
    }));
  }

  if (event.image) {
    schema.image = generateImageUrl(event.image, config);
  }

  if (event.url) {
    schema.url = generateCanonicalUrl(event.url, config);
  }

  return schema;
}

/**
 * Generate JSON-LD structured data for a Person (Artist profile)
 */
export interface PersonData {
  name: string;
  description?: string;
  jobTitle?: string;
  instrument?: string;
  image?: string;
  url?: string;
  sameAs?: string[];
}

export function generatePersonSchema(
  person: PersonData,
  config: SEOConfig = defaultSEOConfig
) {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: person.name,
  };

  if (person.description) {
    schema.description = person.description;
  }

  if (person.jobTitle) {
    schema.jobTitle = person.jobTitle;
  }

  if (person.instrument) {
    schema.additionalProperty = {
      '@type': 'PropertyValue',
      name: 'Instrument',
      value: person.instrument,
    };
  }

  if (person.image) {
    schema.image = generateImageUrl(person.image, config);
  }

  if (person.url) {
    schema.url = generateCanonicalUrl(person.url, config);
  }

  if (person.sameAs && person.sameAs.length > 0) {
    schema.sameAs = person.sameAs;
  }

  return schema;
}

/**
 * Generate JSON-LD structured data for BreadcrumbList
 */
export interface BreadcrumbItem {
  name: string;
  path: string;
}

export function generateBreadcrumbSchema(
  breadcrumbs: BreadcrumbItem[],
  config: SEOConfig = defaultSEOConfig
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: generateCanonicalUrl(item.path, config),
    })),
  };
}

/**
 * Truncate text to a specific length with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength - 3).trim() + '...';
}

/**
 * Clean and format description for meta tags
 */
export function formatMetaDescription(description: string, maxLength: number = 160): string {
  // Remove HTML tags
  const cleanText = description.replace(/<[^>]*>/g, '');
  // Remove extra whitespace
  const normalizedText = cleanText.replace(/\s+/g, ' ').trim();
  // Truncate if needed
  return truncateText(normalizedText, maxLength);
}